import React, { useState, useEffect } from 'react';
import API from '../utils/api';
import { registerPasskey, authenticatePasskey, isWebAuthnAvailable } from '../utils/webauthn';
import './TwoFactorSetup.css';

const TwoFactorSetup = () => {
  const [tab, setTab] = useState('authenticator');
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Authenticator state
  const [setupPhase, setSetupPhase] = useState(null); // null, 'qrcode', 'verify'
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [password, setPassword] = useState('');

  // Passkey state
  const [passkeys, setPasskeys] = useState([]);
  const [newPasskeyNickname, setNewPasskeyNickname] = useState('');
  const [editingPasskeyId, setEditingPasskeyId] = useState(null);

  // Load initial status
  useEffect(() => {
    fetchStatus();
    if (passkeyEnabled) fetchPasskeys();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await API.get('/auth/2fa/status');
      setTwoFaEnabled(res.data.twoFaEnabled);
      setPasskeyEnabled(res.data.passkeyEnabled);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    }
  };

  const fetchPasskeys = async () => {
    try {
      const res = await API.get('/auth/passkeys');
      setPasskeys(res.data.passkeys || []);
    } catch (err) {
      console.error('Failed to fetch passkeys:', err);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // AUTHENTICATOR APP HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const handleStartSetupTOTP = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/auth/2fa/setup-totp', {});
      setQrCode(res.data.qrCode);
      setSetupPhase('qrcode');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTOTP = async () => {
    if (verificationCode.length !== 6) {
      setError('Code must be 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await API.post('/auth/2fa/verify-totp', {
        code: verificationCode
      });

      setSetupPhase(null);
      setVerificationCode('');
      setTwoFaEnabled(true);
      setSuccess('2FA enabled successfully!');

      // Get backup codes
      const backupRes = await API.get('/auth/2fa/backup-codes');
      setBackupCodes(backupRes.data.codes);
      setSetupPhase('backup-codes');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableTOTP = async () => {
    if (!password) {
      setError('Password required');
      return;
    }

    if (!window.confirm('Are you sure? You\'ll need to set up 2FA again to use it.')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await API.post('/auth/2fa/disable-totp', { password });
      setTwoFaEnabled(false);
      setPassword('');
      setSetupPhase(null);
      setSuccess('2FA disabled');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PASSKEY HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const handleAddPasskey = async () => {
    if (!isWebAuthnAvailable()) {
      setError('Passkeys are not supported on this device or browser');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Get registration options from server
      const optionsRes = await API.post('/auth/passkey/registration-options', {});
      const options = optionsRes.data.options;

      // Use WebAuthn to create credential
      const credential = await registerPasskey(options, newPasskeyNickname || 'My Passkey');

      // Send credential to server
      await API.post('/auth/passkey/register', credential);

      setNewPasskeyNickname('');
      setSuccess('Passkey added successfully!');
      await fetchPasskeys();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add passkey');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePasskey = async (passkeyId) => {
    if (!window.confirm('Delete this passkey?')) return;

    setLoading(true);
    setError('');
    try {
      await API.delete(`/auth/passkey/${passkeyId}`);
      setSuccess('Passkey deleted');
      await fetchPasskeys();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete passkey');
    } finally {
      setLoading(false);
    }
  };

  const handleRenamePasskey = async (passkeyId, newNickname) => {
    if (!newNickname.trim()) {
      setError('Nickname cannot be empty');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await API.patch(`/auth/passkey/${passkeyId}`, { nickname: newNickname });
      setSuccess('Passkey renamed');
      setEditingPasskeyId(null);
      await fetchPasskeys();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename passkey');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="two-factor-setup">
      <h2>Security Settings</h2>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="tabs">
        <button
          className={`tab-button ${tab === 'authenticator' ? 'active' : ''}`}
          onClick={() => setTab('authenticator')}
        >
          Authenticator App
        </button>
        <button
          className={`tab-button ${tab === 'passkeys' ? 'active' : ''}`}
          onClick={() => setTab('passkeys')}
        >
          Passkeys
        </button>
      </div>

      {/* AUTHENTICATOR TAB */}
      {tab === 'authenticator' && (
        <div className="tab-content">
          {!twoFaEnabled ? (
            <div className="section">
              <h3>Set Up Authenticator App</h3>
              <p>Use an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator</p>

              {!setupPhase && (
                <button
                  className="btn btn-primary"
                  onClick={handleStartSetupTOTP}
                  disabled={loading}
                >
                  {loading ? 'Setting up...' : 'Enable 2FA'}
                </button>
              )}

              {setupPhase === 'qrcode' && (
                <div className="setup-steps">
                  <div className="step">
                    <h4>Step 1: Scan QR Code</h4>
                    {qrCode && (
                      <div className="qr-code-container">
                        <img src={qrCode} alt="QR Code" />
                      </div>
                    )}
                    <p>Scan this code with your authenticator app</p>
                  </div>

                  <div className="step">
                    <h4>Step 2: Verify Code</h4>
                    <p>Enter the 6-digit code from your authenticator app</p>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="000000"
                      maxLength="6"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="code-input"
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleVerifyTOTP}
                      disabled={loading || verificationCode.length !== 6}
                    >
                      {loading ? 'Verifying...' : 'Verify'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setSetupPhase(null);
                        setQrCode('');
                        setVerificationCode('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {setupPhase === 'backup-codes' && (
                <div className="backup-codes-section">
                  <h4>Save Your Backup Codes</h4>
                  <p className="warning">⚠️ Save these codes in a safe place. You can use them to access your account if you lose your authenticator.</p>
                  <div className="backup-codes-list">
                    {backupCodes.map((code, idx) => (
                      <code key={idx} className="backup-code">{code}</code>
                    ))}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      const text = backupCodes.join('\n');
                      navigator.clipboard.writeText(text);
                      setSuccess('Backup codes copied to clipboard');
                    }}
                  >
                    Copy All Codes
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setSetupPhase(null);
                      setBackupCodes([]);
                      setSuccess('');
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="section">
              <div className="status-box">
                <h3>✅ Authenticator App Enabled</h3>
                <p>You'll be asked for a code from your authenticator app when you log in</p>
              </div>

              <div className="disable-section">
                <h4>Disable 2FA</h4>
                <p>Enter your password to disable 2FA</p>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                />
                <button
                  className="btn btn-danger"
                  onClick={handleDisableTOTP}
                  disabled={loading || !password}
                >
                  {loading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PASSKEYS TAB */}
      {tab === 'passkeys' && (
        <div className="tab-content">
          {!isWebAuthnAvailable() ? (
            <div className="alert alert-warning">
              Passkeys are not supported on this browser or device
            </div>
          ) : (
            <div className="section">
              <h3>Passkeys</h3>
              <p>Use your device biometric or PIN to sign in without a password</p>

              <div className="add-passkey">
                <input
                  type="text"
                  placeholder="Nickname (e.g., My iPhone)"
                  value={newPasskeyNickname}
                  onChange={(e) => setNewPasskeyNickname(e.target.value)}
                  className="form-input"
                />
                <button
                  className="btn btn-primary"
                  onClick={handleAddPasskey}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Passkey'}
                </button>
              </div>

              {passkeys.length > 0 && (
                <div className="passkeys-list">
                  <h4>Your Passkeys ({passkeys.length})</h4>
                  {passkeys.map((passkey) => (
                    <div key={passkey.id} className="passkey-item">
                      {editingPasskeyId === passkey.id ? (
                        <div className="passkey-edit">
                          <input
                            type="text"
                            defaultValue={passkey.nickname}
                            id={`edit-${passkey.id}`}
                            className="form-input"
                          />
                          <button
                            className="btn btn-small btn-primary"
                            onClick={() =>
                              handleRenamePasskey(
                                passkey.id,
                                document.getElementById(`edit-${passkey.id}`).value
                              )
                            }
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => setEditingPasskeyId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="passkey-info">
                          <div>
                            <strong>{passkey.nickname}</strong>
                            <small>Created: {new Date(passkey.created_at).toLocaleDateString()}</small>
                            {passkey.last_used_at && (
                              <small>Last used: {new Date(passkey.last_used_at).toLocaleDateString()}</small>
                            )}
                          </div>
                          <div className="passkey-actions">
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() => setEditingPasskeyId(passkey.id)}
                            >
                              Rename
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeletePasskey(passkey.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;
